import { PlayerViewTab } from 'modules/players/domain/enums/player-view-tab';
import { KitType } from 'shared/kit/kit-type';
import { TeamViewTab } from 'modules/teams/domain/enums/team-view-tab';
import { ChampionshipEditTab } from 'modules/championship/domain/enums/championship-edit-tab';
import { ChampionshipMediaTab } from 'modules/championship/domain/enums/championship-media-tab';
import { ChampionshipStageTab } from 'modules/championship/domain/enums/championship-stage-tab';
import { StageGridTab } from 'modules/championship/domain/enums/stage-grid-tab';
import { StageMatchTab } from 'modules/championship/domain/enums/stage-match-tab';
import { SUBSCRIPTION_FEATURE_QUERY } from 'modules/organizer/domain/constants/wallet-topup';
import type { appFeature } from 'modules/organizer/domain/interfaces/available-feature';
import { CREATE_TEMPLATE_QUERY } from 'modules/manager/domain/create-template-query';
import type { MediaKind } from 'modules/manager/domain/media-kind';

/**
 * Шаблоны адресов вкладки «Этапы».
 *
 * Вынесены отдельным `as const`: `useMatch` выводит имена параметров из строкового
 * литерала, а свойство обычного объекта расширяется до `string` — и тогда
 * `match.params.stageId` перестаёт существовать для типов.
 */
const CHAMPIONSHIP_STEPS_ROUTES = {
  INDEX: '/championships/:id/steps',
  CREATE: '/championships/:id/steps/create',
  /** Этап на подвкладке «Редактирование» — корень без дополнительного сегмента. */
  STAGE: '/championships/:id/steps/:stageId',
  TEAMS: '/championships/:id/steps/:stageId/teams',
  GRID: '/championships/:id/steps/:stageId/grid',
  GRID_STANDING: '/championships/:id/steps/:stageId/grid/standing',
  GRID_MATRIX: '/championships/:id/steps/:stageId/grid/matrix',
  MATCHES: '/championships/:id/steps/:stageId/matches',
  MATCH: '/championships/:id/steps/:stageId/matches/:gameId',
  MATCH_RESULT: '/championships/:id/steps/:stageId/matches/:gameId/result',
  MATCH_MOMENTS: '/championships/:id/steps/:stageId/matches/:gameId/moments',
} as const;

export const Pages = {
  SIGN_IN: '/sign-in',
  SIGN_UP: '/sign-up',
  SELECT_TOURNAMENT: '/select-tournament',
  /** Согласие на доступ AI-клиента (MCP): сюда бэкенд ведёт с параметрами своего `/oauth/authorize`. */
  OAUTH_CONSENT: '/oauth-consent',
  INDEX: '/',
  ABOUT: '/about',
  POLICY: '/policy',
  TERMS: '/terms',
  SETTINGS: {
    INDEX: '/settings',
    SOCIAL_NET: '/settings/social-net',
    SUBSCRIPTION: '/settings/subscription',
    /** Личный кабинет: данные пользователя и язык интерфейса. */
    ACCOUNT: '/settings/account',
    /** Вкладка «Интерфейс»: включение новых версий экранов для этого браузера. */
    INTERFACE: '/settings/interface',
    /** Вкладка «Интеграции»: подключение Telegram-канала организации для публикации постов. */
    INTEGRATIONS: '/settings/integrations',
    builders: {
      /** С фичей вкладка сама подставит сумму и тариф — самый дешёвый из тех, где эта фича есть. */
      subscription: (feature?: appFeature) =>
        feature ? `/settings/subscription?${SUBSCRIPTION_FEATURE_QUERY}=${feature}` : '/settings/subscription',
    },
  },
  ORGANIZER: {
    CREATE: '/organizer/create',
  },
  TEMPLATE: {
    INDEX: '/template/',
    BOARDS: '/template/:templateId/boards',
    EDITOR: '/template/:templateId/boards/:boardId',
    PREVIEW: '/template/:templateId/boards/:boardId/preview',
    builders: {
      boards: (templateId: number) => `/template/${templateId}/boards`,
      editor: (templateId: number, boardId: number) => `/template/${templateId}/boards/${boardId}`,
      preview: (templateId: number, boardId: number) => `/template/${templateId}/boards/${boardId}/preview`,
    },
    CATALOG: {
      INDEX: '/template/catalog',
      CARD: '/template/catalog/:id',
      builders: {
        card: (id: number) => `/template/catalog/${id}`,
      },
    },
  },
  SUPPORT: '/support',
  CHAMPIONSHIPS: {
    INDEX: '/championships',
    CREATE: '/championships/create/common',
    EDIT: '/championships/:id',
    builders: {
      edit: (id: number, hash: string = ChampionshipEditTab.COMMON) => `/championships/${id}/${hash}`,
    },
    /**
     * Вкладка «Этапы» и всё, что под ней. `steps` здесь — это `stage` из домена,
     * сущности «шаг» не существует; см. глоссарий.
     *
     * Шаблоны нужны не только для `<Route path>`, но и для `useMatch` в табах и
     * хлебных крошках, поэтому перечислены целиком, а не собираются на месте.
     */
    STEPS: {
      ...CHAMPIONSHIP_STEPS_ROUTES,
      builders: {
        index: (championshipId: number) => `/championships/${championshipId}/steps`,
        create: (championshipId: number) => `/championships/${championshipId}/steps/create`,
        stage: (championshipId: number, stageId: number, tab?: ChampionshipStageTab) =>
          `/championships/${championshipId}/steps/${stageId}${tab ? `/${tab}` : ''}`,
        grid: (championshipId: number, stageId: number, tab: StageGridTab) =>
          `/championships/${championshipId}/steps/${stageId}/grid/${tab}`,
        match: (championshipId: number, stageId: number, gameId: number, tab: StageMatchTab = StageMatchTab.RESULT) =>
          `/championships/${championshipId}/steps/${stageId}/matches/${gameId}/${tab}`,
      },
    },
    /** Вкладка «Медиа» и её подвкладки «Картинки» / «Шаблоны» (`ChampionshipMediaTab`). */
    MEDIA: {
      INDEX: '/championships/:id/media',
      IMAGES: '/championships/:id/media/images',
      TEMPLATES: '/championships/:id/media/templates',
      builders: {
        tab: (championshipId: number, tab: ChampionshipMediaTab) => `/championships/${championshipId}/media/${tab}`,
      },
    },
  },
  /** Старый адрес соревнований. Оставлен только для редиректа — новых ссылок на него не строим. */
  LEGACY_TOURNEYS: '/tourneys',
  GAMES: 'games',
  TEAMS: {
    INDEX: '/teams/',
    CREATE: '/teams/create',
    EDIT: '/teams/:id',
    builders: {
      edit: (id: number, hash: string = TeamViewTab.COMMON) => `/teams/${id}/${hash}`,
      /** Конкретный комплект формы команды: `/teams/:id/kits/:kitType`. */
      editKit: (id: number, kitType: KitType = KitType.HOME) => `/teams/${id}/${TeamViewTab.KITS}/${kitType}`,
    },
  },
  OVERLAY_V2: {
    INDEX: 'organizer/:organizerId/device/:deviceId/overlay',
    CONTROL_PANEL: 'organizer/:organizerId/device/:deviceId/control-panel',
    builders: {
      // `options` нужны панели управления, когда она открывает окно оверлея:
      // конкретное табло, запрет на его смену извне и скрытый джойстик.
      // `lang` — язык чеклиста и пульта: OBS сам отдаёт язык своего интерфейса, а не организатора
      // (см. `LOCALE_QUERY_PARAM` в `shared/i18n/detect-locale.ts`).
      // `hash` — фрагмент без `#`, его браузер на сервер не отправляет: в нём едет подключение
      // видеометок вместе с паролем OBS (`video-markers/domain/utils/recorder-config-fragment.ts`).
      index: (
        organizerId: string,
        deviceId: string,
        token: string,
        options?: { boardId?: number; locked?: boolean; hidePanel?: boolean; lang?: string; hash?: string },
      ) => {
        const search = new URLSearchParams({ token });
        if (options?.boardId !== undefined) search.append('boardId', String(options.boardId));
        if (options?.locked !== undefined) search.append('locked', String(options.locked));
        if (options?.hidePanel !== undefined) search.append('hidePanel', String(options.hidePanel));
        if (options?.lang !== undefined) search.append('lang', options.lang);
        const hash = options?.hash ? `#${options.hash}` : '';
        return `/organizer/${organizerId}/device/${deviceId}/overlay?${search.toString()}${hash}`;
      },
      controlPanel: (organizerId: string, deviceId: string, token: string, options?: { lang?: string }) => {
        const search = new URLSearchParams({ token });
        if (options?.lang !== undefined) search.append('lang', options.lang);
        return `/organizer/${organizerId}/device/${deviceId}/control-panel?${search.toString()}`;
      },
    },
  },
  ORGANIZERS: {
    INDEX: '/organizers/',
    VIEW: '/organizers/:id',
    builders: {
      view: (id: number) => `/organizers/${id}`,
    },
  },
  PLANS: {
    INDEX: '/plans',
    VIEW: '/plans/:id',
    builders: {
      view: (id: number) => `/plans/${id}`,
    },
  },
  SCHEDULE: {
    INDEX: '/schedule/',
  },
  PLAYERS: {
    INDEX: '/players/',
    VIEW: '/players/:id',
    CREATE: '/players/create/',
    builders: {
      view: (id: number, hash: string = PlayerViewTab.COMMON) => `/players/${id}/${hash}`,
    },
  },
  MASTER_BOARDS: {
    INDEX: '/master-board',
    CONTROL_PANEL: '/master-board/control-panel/:id?',
    BOARD: '/master-board/board/:id?',
    builders: {
      controlPanel: (id: string) => `/master-board/control-panel${id ? `/${id}` : ''}`,
      board: (id: string) => `/master-board/board${id ? `/${id}` : ''}`,
    },
  },
  LOCATIONS: {
    INDEX: '/locations',
    CREATE: '/locations/create',
    VIEW: '/locations/:id',
    builders: {
      edit: (id: number) => `/locations/${id}`,
    },
  },
  APPLICATION_LINK: {
    INDEX: '/application-link',
    SUCCESS: '/success',
  },
  NOTIFICATIONS: {
    INDEX: '/notifications',
    CREATE: '/notifications/create',
    EDIT: '/notifications/edit/:id',
    builders: {
      edit: (id: number) => `/notifications/edit/${id}`,
    },
  },
  UI_KIT: {
    INDEX: '/ui-kit',
    GROUP: '/ui-kit/:group',
    DEMO: '/ui-kit/:group/:component',
    builders: {
      group: (group: string) => `/ui-kit/${group}`,
      demo: (group: string, component: string) => `/ui-kit/${group}/${component}`,
    },
  },
  MIGRATION: {
    INDEX: '/migration',
  },
  CLIENT_TOURNAMENT: {
    VIEW_CHAMPIONSHIP: '/client-tournament/:idTournament/:idChampionship',
    builders: {
      viewChampionship: (idTournament: number, idChampionship: number) => `/client-tournament/${idTournament}/${idChampionship}`,
    },
  },
  ROLES_MANAGER: {
    INDEX: '/roles-manager',
    builders: {
      index: () => `/roles-manager`,
    },
  },
  DEVICES: {
    INDEX: '/devices',
    VIDEO_MARKERS_GUIDE: '/devices/video-markers',
    builders: {
      /** Инструкция одной программы: `/devices/video-markers/obs` или `/vmix`. */
      videoMarkersGuide: (program: 'obs' | 'vmix') => `/devices/video-markers/${program}`,
    },
  },
  ANALYTICS: {
    INDEX: '/analytics',
  },
  CHAT: {
    INDEX: '/chat',
  },
  MEDIA_MANAGER: {
    INDEX: '/manager',
    TEMPLATES_LIBRARY: '/manager/library',
    GROUP: '/manager/groups/:templateGroupId',
    EDITOR: '/manager/templates/:templateId',
    QUEUE: '/manager/queue',
    builders: {
      /** With `create` the group page opens «Создать шаблон» with that media kind (IMGM-1518). */
      group: (templateGroupId: number, options?: { create?: MediaKind }) =>
        options?.create
          ? `/manager/groups/${templateGroupId}?${CREATE_TEMPLATE_QUERY}=${options.create}`
          : `/manager/groups/${templateGroupId}`,
      editor: (templateId: number) => `/manager/templates/${templateId}`,
      queue: () => '/manager/queue',
    },
  },
};
